import { useEffect, useMemo, useState } from 'react';
import type { Account } from '../../core/accounts';
import { buildTree, type Category } from '../../core/categories';
import { formatDateKey } from '../../core/dates';
import { EMPTY_FILTERS, applyFilters, type Filters } from '../../core/filters';
import { formatPaise } from '../../core/money';
import type { Period } from '../../core/period';
import {
  describeReclassification,
  isEmptyChange,
  planReclassify,
  validateReclassification,
  type Reclassification,
} from '../../core/reclassify';
import type { Transaction } from '../../core/transactions';
import { watchAccounts } from '../../data/accounts';
import type { UserRecord } from '../../data/auth';
import { watchCategories, watchTags, type Tag } from '../../data/categories';
import { applyReclassification } from '../../data/reclassify';
import { queryForFilters, queryKey, watchTransactions } from '../../data/transactions';
import { FilterBar } from '../components/FilterBar';

/**
 * How many rows the table draws.
 *
 * Selection is not capped — that is the point of WD-06's warning. A year is ~2,000
 * transactions and drawing all of them costs more than it tells anyone, so the table
 * stops here and says so, while "select all matching" still means all of them.
 */
const RENDER_CAP = 200;

/** Above this, a mass edit gets an explicit confirmation rather than a single click. */
const CONFIRM_ABOVE = 25;

const CLEAR = '__clear__';

interface Props {
  record: UserRecord;
  period: Period;
}

/**
 * Bulk reclassify (FR-CAT-09) — the screen that cleans up the **180 imported rows that
 * say nothing but "Expenses"**.
 *
 * Desktop-first by WIREFRAMES §5: it is a review task, done sitting down, against a wide
 * table. It works on a phone but is not designed for it.
 */
export function ReclassifyScreen({ record, period }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);

  // Opens on the worklist: uncategorised rows are what this screen exists for.
  const [filters, setFilters] = useState<Filters>(() => EMPTY_FILTERS(period));
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [addTagId, setAddTagId] = useState('');
  const [removeTagId, setRemoveTagId] = useState('');
  const [clearFlag, setClearFlag] = useState(false);

  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<{ written: number; total: number } | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchAccounts(setAccounts), []);
  useEffect(() => watchCategories(setCategories), []);
  useEffect(() => watchTags(setTags), []);
  useEffect(() => setFilters((f) => ({ ...f, period })), [period]);

  // One place decides what Firestore is asked for, shared with the transaction list and
  // reports (`queryForFilters`). `spec` is rebuilt every render, so the effect depends on
  // its stable string form rather than on its identity.
  // Keyed on the query's own shape, not on `filters`: typing in the search box changes
  // the filters but not what Firestore is asked for, and re-subscribing on every
  // keystroke would flash the list back to "Loading…" for nothing.
  const specKey = queryKey(queryForFilters(filters));
  const spec = useMemo(() => queryForFilters(filters), [specKey]);
  useEffect(() => {
    setTransactions(null);
    setSelected(new Set());
    return watchTransactions(
      spec,
      (list) => {
        setTransactions(list);
        setError(null);
      },
      (cause) => {
        setTransactions([]);
        setError(cause.message);
      },
    );
  }, [spec]);

  const accountType = useMemo(() => {
    const map = new Map(accounts.map((a) => [a.id, a.type]));
    return (id: string) => map.get(id);
  }, [accounts]);

  const matching = useMemo(
    () => (transactions ? applyFilters(transactions, filters) : []),
    [transactions, filters],
  );

  // A row can drop out of the filtered set while still selected — narrowing the filter
  // must not leave an invisible row queued for a change nobody can see.
  const chosen = useMemo(
    () => matching.filter((t) => selected.has(t.id)),
    [matching, selected],
  );

  const change: Reclassification = useMemo(() => {
    const next: Reclassification = {};
    if (categoryId === CLEAR) next.categoryId = null;
    else if (categoryId) next.categoryId = categoryId;
    if (subcategoryId) next.subcategoryId = subcategoryId;
    if (addTagId) next.addTagIds = [addTagId];
    if (removeTagId) next.removeTagIds = [removeTagId];
    if (clearFlag) next.clearNeedsEnrichment = true;
    return next;
  }, [categoryId, subcategoryId, addTagId, removeTagId, clearFlag]);

  const problem = isEmptyChange(change) ? null : validateReclassification(change, categories);

  const plan = useMemo(
    () => (problem || isEmptyChange(change) ? null : planReclassify(chosen, change, categories)),
    [chosen, change, categories, problem],
  );

  const expenseTree = useMemo(() => buildTree(categories, 'expense'), [categories]);
  const incomeTree = useMemo(() => buildTree(categories, 'income'), [categories]);
  const subcategories = useMemo(
    () =>
      categoryId && categoryId !== CLEAR
        ? categories.filter((c) => c.parentId === categoryId && !c.archived)
        : [],
    [categories, categoryId],
  );

  const nameOf = (id: string | null) => (id ? (accounts.find((a) => a.id === id)?.name ?? id) : '—');
  const categoryLabel = (txn: Transaction) => {
    if (txn.type === 'transfer') return <span className="muted tiny">transfer</span>;
    if (!txn.categoryId) return <span className="muted tiny">uncategorised</span>;
    const name = categories.find((c) => c.id === txn.categoryId)?.name ?? txn.categoryId;
    const sub = txn.subcategoryId
      ? (categories.find((c) => c.id === txn.subcategoryId)?.name ?? txn.subcategoryId)
      : null;
    return sub ? `${name} → ${sub}` : name;
  };

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setConfirming(false);
  }

  /** Select-all covers the whole filtered set, not the drawn page (WIREFRAMES §5). */
  function toggleAll() {
    setSelected((current) =>
      current.size === matching.length ? new Set() : new Set(matching.map((t) => t.id)),
    );
    setConfirming(false);
  }

  function resetAction() {
    setCategoryId('');
    setSubcategoryId('');
    setAddTagId('');
    setRemoveTagId('');
    setClearFlag(false);
    setConfirming(false);
  }

  async function apply() {
    if (!plan || plan.changes.length === 0) return;
    if (plan.changes.length > CONFIRM_ABOVE && !confirming) {
      setConfirming(true);
      return;
    }

    setError(null);
    setDone(null);
    setBusy({ written: 0, total: plan.changes.length });
    try {
      const changed = await applyReclassification(plan, record.uid, accountType, setBusy);
      setDone(`${changed} transaction${changed === 1 ? '' : 's'} updated.`);
      setSelected(new Set());
      resetAction();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not apply that.');
    } finally {
      setBusy(null);
    }
  }

  const sentence =
    plan && !isEmptyChange(change)
      ? describeReclassification(change, categories, (id) => tags.find((t) => t.id === id)?.name ?? id)
      : '';

  return (
    <>
      <div className="head-row">
        <h1>Bulk reclassify</h1>
        <button
          type="button"
          className="btn btn-quiet"
          onClick={() => setFilters((f) => ({ ...f, categoryId: undefined, needsEnrichment: true }))}
        >
          Show the follow-up queue
        </button>
      </div>

      <FilterBar
        filters={filters}
        accounts={accounts}
        categories={categories}
        tags={tags}
        onChange={(next) => {
          setFilters(next);
          setConfirming(false);
        }}
      />

      {error && <p className="error">{error}</p>}
      {done && <p className="ok">{done}</p>}

      {transactions === null ? (
        <p className="muted">Loading…</p>
      ) : matching.length === 0 ? (
        <div className="placeholder">
          {transactions.length === 0
            ? 'No transactions in this period.'
            : 'Nothing matches these filters.'}
        </div>
      ) : (
        <>
          {/* Six columns of tabular data do not fit a narrow window. The table scrolls
              inside this, so the page itself never scrolls sideways. */}
          <div className="table-scroll">
          <table className="table reclassify-table">
            <thead>
              <tr>
                <th className="pick">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === matching.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selected.size > 0 && selected.size < matching.length;
                    }}
                    onChange={toggleAll}
                    aria-label={`Select all ${matching.length} matching transactions`}
                  />
                </th>
                <th>Date</th>
                <th>Account</th>
                <th>Description</th>
                <th>Category</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {matching.slice(0, RENDER_CAP).map((txn) => (
                <tr
                  key={txn.id}
                  className={selected.has(txn.id) ? 'selected' : undefined}
                  onClick={() => toggle(txn.id)}
                >
                  <td className="pick">
                    <input
                      type="checkbox"
                      checked={selected.has(txn.id)}
                      onChange={() => toggle(txn.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${txn.description ?? 'transaction'} on ${txn.dateKey}`}
                    />
                  </td>
                  <td className="date">{formatDateKey(txn.dateKey, 'dd MMM')}</td>
                  <td>{nameOf(txn.accountId)}</td>
                  <td>
                    {txn.description || '—'}
                    {txn.needsEnrichment && <span className="chip tiny"> follow-up</span>}
                  </td>
                  <td>{categoryLabel(txn)}</td>
                  <td className="num">{formatPaise(txn.amountPaise, { symbol: false })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {matching.length > RENDER_CAP && (
            <p className="muted tiny">
              Showing the first {RENDER_CAP} of {matching.length}. Select-all still covers all{' '}
              {matching.length}.
            </p>
          )}
        </>
      )}

      {/* The action bar. Sticky, because the selection is made by scrolling a long table
          and the controls should not be at the bottom of it. */}
      <div className="reclassify-actions">
        <div className="row">
          <strong>
            {chosen.length === 0
              ? 'Nothing selected'
              : `${chosen.length} of ${matching.length} selected`}
          </strong>

          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setSubcategoryId('');
              setConfirming(false);
            }}
            aria-label="Set category"
          >
            <option value="">Set category…</option>
            <option value={CLEAR}>— clear the category —</option>
            <optgroup label="Expense categories">
              {expenseTree.map((node) => (
                <option key={node.category.id} value={node.category.id}>
                  {node.category.name}
                </option>
              ))}
            </optgroup>
            {incomeTree.length > 0 && (
              <optgroup label="Income heads">
                {incomeTree.map((node) => (
                  <option key={node.category.id} value={node.category.id}>
                    {node.category.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {subcategories.length > 0 && (
            <select
              value={subcategoryId}
              onChange={(e) => {
                setSubcategoryId(e.target.value);
                setConfirming(false);
              }}
              aria-label="Set subcategory"
            >
              <option value="">No subcategory</option>
              {subcategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          {tags.length > 0 && (
            <>
              <select
                value={addTagId}
                onChange={(e) => {
                  setAddTagId(e.target.value);
                  setConfirming(false);
                }}
                aria-label="Add tag"
              >
                <option value="">Add tag…</option>
                {tags.filter((t) => !t.archived).map((t) => (
                  <option key={t.id} value={t.id}>#{t.name}</option>
                ))}
              </select>
              <select
                value={removeTagId}
                onChange={(e) => {
                  setRemoveTagId(e.target.value);
                  setConfirming(false);
                }}
                aria-label="Remove tag"
              >
                <option value="">Remove tag…</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>#{t.name}</option>
                ))}
              </select>
            </>
          )}

          <label className="inline">
            <input
              type="checkbox"
              checked={clearFlag}
              onChange={(e) => {
                setClearFlag(e.target.checked);
                setConfirming(false);
              }}
            />
            Clear follow-up
          </label>

          <span className="spacer" />

          <button
            type="button"
            className="btn btn-primary"
            disabled={!plan || plan.changes.length === 0 || busy !== null}
            onClick={() => void apply()}
          >
            {busy
              ? `Applying ${busy.written} of ${busy.total}…`
              : confirming
                ? `Yes — change ${plan?.changes.length}`
                : plan
                  ? `Apply to ${plan.changes.length}`
                  : 'Apply'}
          </button>

          {(confirming || !isEmptyChange(change)) && (
            <button type="button" className="btn btn-quiet" onClick={resetAction} disabled={busy !== null}>
              Cancel
            </button>
          )}
        </div>

        {problem && <p className="error tiny">{problem}</p>}

        {plan && (
          <p className={confirming ? 'warn' : 'muted tiny'}>
            {confirming ? (
              <>
                <strong>{plan.changes.length} transactions</strong> will change: {sentence}. This
                writes an audit entry for each one and can only be undone by editing them again.
              </>
            ) : (
              <>
                Will {sentence} on <strong>{plan.changes.length}</strong>
                {plan.unchanged.length > 0 && <> · {plan.unchanged.length} already there</>}
                {plan.skipped.length > 0 && <> · {plan.skipped.length} cannot take it</>}
              </>
            )}
          </p>
        )}

        {/* Say *why* a row is skipped. A count on its own reads as a failure; the reason
            is usually that the selection included transfers, which is expected. */}
        {plan && plan.skipped.length > 0 && !confirming && (
          <ul className="muted tiny skip-reasons">
            {[...new Set(plan.skipped.map((s) => s.reason))].map((reason) => (
              <li key={reason}>
                {plan.skipped.filter((s) => s.reason === reason).length} × {reason}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
